import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  FaTrophy, FaTrash, FaTimes, FaPaperPlane, FaCalendarAlt,
  FaMedal, FaRobot, FaPlusCircle, FaCheckCircle,
  FaMoneyBillWave, FaStopwatch, FaMinusCircle, FaFolderOpen,
  FaList, FaThLarge, FaBolt, FaPaperclip, FaEnvelope,
  FaFileInvoiceDollar, FaMagic, FaBrain, FaBuilding, FaArrowRight,
  FaUserEdit, FaCloudUploadAlt, FaClipboardList, FaCommentDots,
  FaShoppingCart, FaSearch, FaSitemap, FaHistory, FaBan, FaInfoCircle
} from 'react-icons/fa';
import api from '../services/api';
import { toast } from 'react-toastify';
import './GestaoCotacoes.css';

// --- HELPERS ---
const extractDays = (str) => {
  if (!str) return 9999;
  const num = parseInt(String(str).replace(/\D/g, ''), 10);
  return isNaN(num) ? 9999 : num;
};

// ✅ FUNÇÃO CALCTOTAL MAIS ROBUSTA
const calcTotal = (item) => {
  if (!item) return 0;
  const valorUnitario = parseFloat(item.valor) || 0;
  const qtd = parseInt(item.quantidade, 10) || 1;
  return valorUnitario * qtd;
};

// =====================================================
// FornecedorPicker (inline) - busca no cadastro corporativo
// =====================================================
const FornecedorPicker = ({ valueName, onSelect, disabled }) => {
  const [q, setQ] = useState(valueName || '');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [opts, setOpts] = useState([]);

  useEffect(() => {
    setQ(valueName || '');
  }, [valueName]);

  useEffect(() => {
    if (!open) return;

    const t = setTimeout(async () => {
      try {
        setLoading(true);
        const resp = await api.get('/api/corporativo/fornecedores', {
          params: {
            page: 1,
            pageSize: 10,
            search: (q || '').trim(),
            status: 'ativo',
            showDeleted: 0,
          },
        });

        const rows = Array.isArray(resp?.data?.data) ? resp.data.data : [];
        setOpts(rows);
      } catch {
        setOpts([]);
      } finally {
        setLoading(false);
      }
    }, 250);

    return () => clearTimeout(t);
  }, [q, open]);

  return (
    <div style={{ position: 'relative' }}>
      <input
        className="inp-full"
        placeholder="Buscar fornecedor cadastrado..."
        value={q}
        disabled={disabled}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 180)}
      />

      {open && (
        <div
          style={{
            position: 'absolute',
            top: '110%',
            left: 0,
            right: 0,
            zIndex: 50,
            background: '#fff',
            border: '1px solid #d1d9e6',
            borderRadius: 10,
            boxShadow: '0 12px 28px rgba(0,0,0,.12)',
            overflow: 'hidden',
            maxHeight: 260,
            overflowY: 'auto',
          }}
        >
          {loading && (
            <div style={{ padding: 10, fontSize: 12, color: '#64748b' }}>
              Buscando fornecedores...
            </div>
          )}

          {!loading && opts.length === 0 && (
            <div style={{ padding: 10, fontSize: 12, color: '#64748b' }}>
              Nenhum fornecedor encontrado.
            </div>
          )}

          {!loading && opts.map((f) => (
            <button
              key={f.id}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onSelect?.(f)}
              style={{
                width: '100%',
                textAlign: 'left',
                padding: '10px 12px',
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                borderBottom: '1px solid #f1f5f9'
              }}
            >
              <div style={{ fontWeight: 700 }}>{f.razao_social}</div>
              <div style={{ fontSize: 12, color: '#64748b' }}>
                {f.cnpj ? `CNPJ: ${f.cnpj}` : 'Sem CNPJ'}{f.email ? ` • ${f.email}` : ''}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// =====================================================
// CCPicker (inline) - busca no cadastro de Centros de Custo
// =====================================================
const CCPicker = ({ onSelect }) => {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [opts, setOpts] = useState([]);

  useEffect(() => {
    if (!open) return;

    const t = setTimeout(async () => {
      try {
        setLoading(true);
        const resp = await api.get('/api/corporativo/centros-custo', {
          params: { search: (q || '').trim() },
        });
        const ativos = (Array.isArray(resp?.data) ? resp.data : []).filter(c => c.status === 'ativo');
        setOpts(ativos);
      } catch {
        setOpts([]);
      } finally {
        setLoading(false);
      }
    }, 250);

    return () => clearTimeout(t);
  }, [q, open]);

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ position: 'relative' }}>
        <FaSearch style={{ position: 'absolute', top: 12, left: 12, color: '#94a3b8' }} />
        <input
          className="inp-full"
          style={{ paddingLeft: 35 }}
          placeholder="Digite o código ou nome da área..."
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 200)}
        />
      </div>

      {open && (
        <div
          style={{
            position: 'absolute', top: '105%', left: 0, right: 0, zIndex: 60,
            background: '#fff', border: '1px solid #cbd5e1', borderRadius: 8,
            boxShadow: '0 10px 25px rgba(0,0,0,.15)', overflow: 'hidden',
            maxHeight: 250, overflowY: 'auto',
          }}
        >
          {loading && <div style={{ padding: 12, fontSize: 13, color: '#64748b' }}>Buscando Centros de Custo...</div>}
          {!loading && opts.length === 0 && <div style={{ padding: 12, fontSize: 13, color: '#64748b' }}>Nenhum CC ativo encontrado.</div>}
          
          {!loading && opts.map((cc) => (
            <button
              key={cc.id}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { setQ(`${cc.codigo} - ${cc.nome_area}`); setOpen(false); onSelect(cc); }}
              style={{
                width: '100%', textAlign: 'left', padding: '12px 14px',
                border: 'none', borderBottom: '1px solid #f1f5f9', background: '#fff',
                cursor: 'pointer', transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
            >
              <div style={{ fontWeight: 700, color: '#0f172a' }}>{cc.codigo} - {cc.nome_area}</div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                Orçamento Restante: R$ {Number((cc.orcamento_anual || 0) - (cc.orcamento_consumido || 0)).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// --- COMPONENTE DE CARD DA LISTAGEM ---
const QuoteCard = ({ quote, onClick, isAdmin, onDelete }) => {
  const getStatusColor = (s) => {
    switch (s) {
      case 'Aprovada': return 'success';
      case 'Pendente': return 'warning';
      case 'Cancelada': return 'danger';
      default: return 'neutral';
    }
  };

  const statusClass = getStatusColor(quote.status);
  const itensSeguros = quote.itens || [];
  
  const menorValor = itensSeguros.length > 0
    ? Math.min(...itensSeguros.map(calcTotal))
    : 0;

  return (
    <div className="quote-list-card" onClick={onClick}>
      <div className={`quote-status-line ${statusClass}`}></div>

      <div className="quote-header-row">
        <div className={`quote-icon-box ${statusClass}`}>
          <FaFileInvoiceDollar />
        </div>
        <div className="quote-badges-top">
          <span className={`badge-pill ${statusClass}`}>{quote.status}</span>
        </div>
      </div>

      <div className="quote-info">
        <h4>{quote.titulo}</h4>
        <p className="quote-date"><FaCalendarAlt /> {new Date(quote.data_criacao).toLocaleDateString()}</p>

        <div className="quote-metrics">
          <div className="metric-item">
            <small>Fornecedores</small>
            <strong>{itensSeguros.length}</strong>
          </div>
          {menorValor > 0 && (
            <div className="metric-item highlight">
              <small>Melhor Oferta (Total)</small>
              <strong>R$ {menorValor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</strong>
            </div>
          )}
          {quote.numero_po && (
            <div className="metric-item" style={{ background: '#e0f2fe', color: '#0369a1', borderColor: '#bae6fd' }}>
              <small>Pedido Vinculado</small>
              <strong>{quote.numero_po}</strong>
            </div>
          )}
        </div>
      </div>

      {isAdmin && (
        <button className="btn-delete-mini" onClick={(e) => onDelete(e, quote.id)} title="Excluir Disputa">
          <FaTrash />
        </button>
      )}
    </div>
  );
};

export default function GestaoCotacoes({ user }) {
  const [cotacoes, setCotacoes] = useState([]);
  const [loading, setLoading] = useState(true);

  // UI States
  const [viewMode, setViewMode] = useState('grid');
  const [isCreating, setIsCreating] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);

  // AI States
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  // PO Handoff States (CC Picker)
  const [showPoModal, setShowPoModal] = useState(false);
  const [selectedWinnerForPo, setSelectedWinnerForPo] = useState(null);
  const [selectedCC, setSelectedCC] = useState(null);
  const [justificativaHandoff, setJustificativaHandoff] = useState('');
  const [condicaoPagamentoHandoff, setCondicaoPagamentoHandoff] = useState(''); 
  const [dataEmissaoHandoff, setDataEmissaoHandoff] = useState(new Date().toISOString().split('T')[0]); 

  // Email Modal State
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailData, setEmailData] = useState({
    to: '', subject: '', body: '', attachmentName: '', winnerName: '', winnerPrice: '', customFile: null, itemId: null
  });

  // Tracking Modal State (Acompanhar Cotação)
  const [showTrackingModal, setShowTrackingModal] = useState(false);
  const [selectedTrackingItem, setSelectedTrackingItem] = useState(null);

  const emptyItem = { fornecedor_id: null, fornecedor_nome: '', fornecedor: '', email_fornecedor: '', valor: '', quantidade: 1, prazo: '', obs: '', arquivo: null };

  const [newQuoteData, setNewQuoteData] = useState({
    titulo: '', observacoes: '', requisicao_id: null, itens: [ { ...emptyItem }, { ...emptyItem } ]
  });
  
  // ✅ NOVO: Estado para rastrear se a cotação atual veio de uma requisição
  const [isFromReq, setIsFromReq] = useState(false);

  const isAdmin = useMemo(() => {
    if (!user || !user.perfil) return false;
    const perfil = user.perfil.toLowerCase();
    return ['admin', 'admin_geral'].includes(perfil);
  }, [user]);

  const fetchCotacoes = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/cotacoes');
      const data = (response.data || []).map(q => ({
        ...q,
        itens: (q.itens || []).map(it => ({
          ...it,
          fornecedor_nome: it.fornecedor_nome || it.fornecedor || '',
        }))
      }));
      setCotacoes(Array.isArray(data) ? data : []);
      
      // ✅ Atualiza o selectedQuote em tempo real (para os modais abertos)
      if (selectedQuote) {
        const updatedSelected = data.find(c => c.id === selectedQuote.id);
        if (updatedSelected) setSelectedQuote(updatedSelected);
      }
    } catch (error) {
      console.error(error);
      toast.error("Erro ao carregar cotações");
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => { fetchCotacoes(); }, []);
  

  // ✅ NOVA FUNÇÃO: Busca atualizações no fundo, sem acionar o "Loading" e piscar a tela
  const fetchCotacoesSilent = async () => {
    try {
      const response = await api.get('/api/cotacoes');
      const data = (response.data || []).map(q => ({
        ...q,
        itens: (q.itens || []).map(it => ({
          ...it,
          fornecedor_nome: it.fornecedor_nome || it.fornecedor || '',
        }))
      }));
      setCotacoes(Array.isArray(data) ? data : []);
      
      // Atualiza a cotação principal
      if (selectedQuote) {
        const updatedSelected = data.find(c => c.id === selectedQuote.id);
        if (updatedSelected) {
          setSelectedQuote(updatedSelected);
          
          // 🔥 A MÁGICA: Atualiza o item dentro do modal aberto!
          if (selectedTrackingItem) {
            const updatedItem = updatedSelected.itens.find(i => i.id === selectedTrackingItem.id);
            if (updatedItem) setSelectedTrackingItem(updatedItem);
          }
        }
      }
    } catch (error) {
      // Falha silenciosa: se der erro de rede, não exibe toast vermelho para o usuário
    }
  };

  // ✅ NOVO EFFECT: Gatilho do Silent Polling
  useEffect(() => {
    let interval;
    // Só fica checando o banco se o modal de rastreio estiver visível
    if (showTrackingModal) {
      interval = setInterval(() => {
        fetchCotacoesSilent();
      }, 3000); // 3000 milissegundos = 3 segundos
    }
    // Quando fechar o modal, ele para de checar para economizar memória
    return () => clearInterval(interval);
  }, [showTrackingModal, selectedQuote, selectedTrackingItem]);


  // ✅ NOVO: Captura redirecionamento vindo da aba de Requisições (Compras)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('fromReq') === '1') {
      try {
        const draftReq = JSON.parse(localStorage.getItem('quoteDraftFromReq'));
        if (draftReq) {
          setNewQuoteData(prev => ({
            ...prev,
            titulo: draftReq.titulo || '',
            observacoes: draftReq.observacoes || '',
            requisicao_id: draftReq.requisicao_id
          }));
          setIsFromReq(true);
          setIsCreating(true); 
          toast.info('Nova disputa iniciada a partir de Requisição Interna.');
        }
        params.delete('fromReq');
        window.history.replaceState({}, '', `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`);
      } catch (e) {}
    }
  }, []);

  useEffect(() => {
    if (!showModal) {
      setAiAnalysis(null);
      setAiLoading(false);
    }
  }, [showModal]);

  const insights = useMemo(() => {
    if (!selectedQuote || !selectedQuote.itens || selectedQuote.itens.length === 0) return null;

    const itensComTotal = selectedQuote.itens.map(i => ({
      ...i, 
      valorUnitario: parseFloat(i.valor) || 0,
      quantidadeReal: parseInt(i.quantidade, 10) || 1,
      valorTotal: calcTotal(i), 
      diasPrazo: extractDays(i.prazo)
    }));

    const valoresValidos = itensComTotal.map(i => i.valorTotal).filter(v => v > 0);
    const prazosValidos = itensComTotal.map(i => i.diasPrazo).filter(d => d !== 9999);

    const menorPreco = valoresValidos.length > 0 ? Math.min(...valoresValidos) : 0;
    const menorPrazo = prazosValidos.length > 0 ? Math.min(...prazosValidos) : 0;
    const media = valoresValidos.length > 0 ? valoresValidos.reduce((acc, curr) => acc + curr, 0) / valoresValidos.length : 0;
    const economiaReais = media - menorPreco;
    const percentualEconomia = media > 0 ? ((economiaReais / media) * 100).toFixed(1) : 0;

    return { menorPreco, menorPrazo, percentualEconomia };
  }, [selectedQuote]);

  const handleCancelCreation = async () => {
    if (isFromReq && newQuoteData.requisicao_id) {
      const confirm = window.confirm(
        "Atenção: Você iniciou esta disputa a partir de uma Requisição do Estoque.\n\nSe você cancelar agora, os dados não serão salvos e a requisição voltará para a fila de Compras (Pendente).\n\nDeseja realmente cancelar?"
      );
      
      if (confirm) {
         try {
           await api.put(`/api/corporativo/estoque/requisicoes/${newQuoteData.requisicao_id}/status`, { status: 'pendente' });
           toast.info("Ação cancelada. A requisição voltou para a fila de compras.");
         } catch(e) {
           toast.error("Erro ao cancelar o status da requisição no banco.");
         }
         localStorage.removeItem('quoteDraftFromReq');
         setIsFromReq(false);
         setIsCreating(false);
         setNewQuoteData({ titulo: '', observacoes: '', requisicao_id: null, itens: [{ ...emptyItem }, { ...emptyItem }] });
      }
    } else {
      setIsCreating(false);
      setNewQuoteData({ titulo: '', observacoes: '', requisicao_id: null, itens: [{ ...emptyItem }, { ...emptyItem }] });
    }
  };

  const handleAddSupplier = () => setNewQuoteData(prev => ({ ...prev, itens: [...prev.itens, { ...emptyItem }] }));
  
  const handleRemoveSupplier = (index) => {
    if (newQuoteData.itens.length <= 2) return toast.warn("Mínimo de 2 fornecedores.");
    setNewQuoteData(prev => ({ ...prev, itens: prev.itens.filter((_, i) => i !== index) }));
  };

  const handleInputChange = (index, field, value) => {
    setNewQuoteData(prev => {
      const novosItens = [...prev.itens];
      const itemAtualizado = { ...novosItens[index] }; 

      if (field === 'quantidade') {
        const num = parseInt(value, 10);
        itemAtualizado[field] = isNaN(num) || num < 1 ? 1 : num;
      } else {
        itemAtualizado[field] = value;
      }
      
      novosItens[index] = itemAtualizado;
      return { ...prev, itens: novosItens };
    });
  };

  const handleFileChange = (index, e) => {
    const file = e.target.files?.[0];
    if (file) {
      setNewQuoteData(prev => {
        const novosItens = [...prev.itens];
        novosItens[index] = { ...novosItens[index], arquivo: file };
        return { ...prev, itens: novosItens };
      });
    }
  };

  const handleSave = async () => {
    if (!newQuoteData.titulo) return toast.warn("Título obrigatório.");
    const validos = newQuoteData.itens.filter(i => i.fornecedor_id && i.valor);
    if (validos.length < 2) return toast.warn("Selecione ao menos 2 fornecedores cadastrados com valores.");

    const formData = new FormData();
    formData.append('titulo', newQuoteData.titulo);
    formData.append('observacoes', newQuoteData.observacoes);
    if (newQuoteData.requisicao_id) formData.append('requisicao_id', newQuoteData.requisicao_id);

    validos.forEach((item, index) => {
      formData.append(`itens[${index}][fornecedor_id]`, String(item.fornecedor_id));
      formData.append(`itens[${index}][fornecedor_nome]`, item.fornecedor_nome || '');
      formData.append(`itens[${index}][fornecedor]`, item.fornecedor_nome || item.fornecedor || '');
      formData.append(`itens[${index}][email_fornecedor]`, item.email_fornecedor || '');
      formData.append(`itens[${index}][valor]`, item.valor);
      formData.append(`itens[${index}][quantidade]`, item.quantidade || 1); 
      formData.append(`itens[${index}][prazo]`, item.prazo || '');
      formData.append(`itens[${index}][obs]`, item.obs || '');
      if (item.arquivo) formData.append(`itens[${index}][arquivo]`, item.arquivo);
    });

    try {
      await api.post('/api/cotacoes', formData);
      toast.success("Disputa lançada com sucesso!");
      localStorage.removeItem('quoteDraftFromReq');
      setIsFromReq(false);
      setIsCreating(false);
      setNewQuoteData({ titulo: '', observacoes: '', requisicao_id: null, itens: [{ ...emptyItem }, { ...emptyItem }] });
      fetchCotacoes();
    } catch (error) {
      toast.error("Erro ao salvar cotação.");
    }
  };

  const handleDelete = async (e, id) => {
    if (e) e.stopPropagation();
    if (!isAdmin) return toast.error("Sem permissão para excluir.");
    if (!window.confirm("Atenção: Isso excluirá permanentemente a cotação. Continuar?")) return;

    try {
      await api.delete(`/api/cotacoes/${id}`);
      setCotacoes(prev => prev.filter(c => c.id !== id));
      toast.success("Excluído com sucesso.");
      if (selectedQuote && selectedQuote.id === id) setShowModal(false);
    } catch (error) {
      toast.error("Erro ao excluir.");
    }
  };

  const handleApprove = async (item) => {
    const nome = item.fornecedor_nome || item.fornecedor || 'Fornecedor';
    if (!window.confirm(`Confirma aprovação oficial para: ${nome}?`)) return;

    setSendingEmail(true);
    try {
      await api.put(`/api/cotacoes/${selectedQuote.id}/aprovar`, { itemId: item.id });
      toast.success(`Vencedor definido: ${nome}`);
      await fetchCotacoes();

      setSelectedQuote(prev => ({
        ...prev, status: 'Aprovada',
        itens: prev.itens.map(i => i.id === item.id ? { ...i, vencedor: true } : i)
      }));
    } catch (error) {
      toast.error("Erro ao registrar aprovação.");
    } finally {
      setSendingEmail(false);
    }
  };

  const handleGenerateAI = async () => {
    if (!selectedQuote) return;
    setAiLoading(true);
    try {
      const response = await api.post('/api/cotacoes/analise-ia', {
        titulo: selectedQuote.titulo, itens: selectedQuote.itens
      });
      setAiAnalysis(response.data.analise);
    } catch (error) {
      toast.error("A IA está indisponível no momento.");
    } finally {
      setAiLoading(false);
    }
  };

  const openEmailModal = (winnerItem) => {
    const nome = winnerItem.fornecedor_nome || winnerItem.fornecedor || '';
    const totalVal = calcTotal(winnerItem); 
    const val = totalVal.toLocaleString('pt-BR', { minimumFractionDigits: 2 });

    setEmailData({
      to: winnerItem.email_fornecedor || '',
      subject: `Formalização: ${selectedQuote.titulo} - Pedido de Compra`,
      body: `Olá ${nome},\n\nParabéns! Informamos que sua proposta para "${selectedQuote.titulo}" foi a vencedora.\n\nVALOR CONFIRMADO: R$ ${val}\n\nPor favor, siga com a emissão da Nota Fiscal e entrega conforme combinado.\n\nQualquer divergência de valores ou prazos, favor responder este e-mail imediatamente.\n\nAtenciosamente,\n\nEquipe de Compras`,
      attachmentName: winnerItem.anexo_url ? 'Proposta_Original.pdf' : null,
      winnerName: nome, winnerPrice: val, customFile: null,
      itemId: winnerItem.id
    });
    setShowEmailModal(true);
  };

  const handleEmailAttachment = (e) => {
    const file = e.target.files?.[0];
    if (file) setEmailData(prev => ({ ...prev, customFile: file }));
  };

  const sendEmailFinal = async () => {
    if (!emailData.to || !emailData.to.includes('@')) return toast.warn("Por favor, insira um e-mail válido.");
    
    const msg = (emailData.customFile) ? "Enviando e-mail com anexo..." : "Enviando e-mail oficial...";
    const toastId = toast.loading(msg);

    try {
      const formData = new FormData();
      formData.append('to', emailData.to);
      formData.append('subject', emailData.subject);
      formData.append('body', emailData.body);
      formData.append('winnerName', emailData.winnerName);
      formData.append('itemId', emailData.itemId);
      
      if (emailData.customFile) {
        formData.append('customFile', emailData.customFile);
      }

      await api.post('/api/cotacoes/enviar-email', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.update(toastId, { render: "Pedido enviado com sucesso!", type: "success", isLoading: false, autoClose: 3000 });
      setShowEmailModal(false);
      fetchCotacoes(); 
      
    } catch (error) {
      console.error(error);
      toast.update(toastId, { render: "Erro ao enviar o e-mail.", type: "error", isLoading: false, autoClose: 3000 });
    }
  };

  // ✅ NOVA FUNÇÃO: Encerrar Conversa e enviar e-mail padrão
  const handleEncerrarConversa = async (item) => {
    const nomeForn = item.fornecedor_nome || item.fornecedor || 'Fornecedor';
    if (!window.confirm(`Tem certeza que deseja encerrar a comunicação com ${nomeForn} e enviar o aviso final?`)) return;

    const toastId = toast.loading('Enviando e-mail de encerramento...');
    try {
      const formData = new FormData();
      formData.append('to', item.email_fornecedor);
      formData.append('subject', `Encerramento de Cotação - ${selectedQuote.titulo}`);
      formData.append('body', `Olá ${nomeForn},\n\nInformamos que a comunicação neste canal para a cotação "${selectedQuote.titulo}" foi oficialmente encerrada pelo nosso sistema.\n\nPara eventuais dúvidas, pendências ou envio de documentação, por favor encaminhe um e-mail para: contato@blbsystem.com.br ou ligue para nossa central.\n\nAtenciosamente,\nEquipe BLB System`);
      formData.append('winnerName', nomeForn);
      formData.append('itemId', item.id);

      await api.post('/api/cotacoes/enviar-email', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.update(toastId, { render: "Conversa encerrada e e-mail enviado!", type: "success", isLoading: false, autoClose: 3000 });
      setShowTrackingModal(false);
      fetchCotacoes();
    } catch (error) {
      console.error(error);
      toast.update(toastId, { render: "Erro ao encerrar a conversa.", type: "error", isLoading: false, autoClose: 3000 });
    }
  };

  const triggerPoGeneration = (winnerItem) => {
    if (!isAdmin) return toast.error('Apenas Administradores podem gerar um Pedido de Compra.');
    if (!winnerItem?.vencedor) return toast.warn('Defina um vencedor antes de gerar um pedido de compra.');
    
    setSelectedWinnerForPo(winnerItem);
    setSelectedCC(null);
    setJustificativaHandoff('');
    setCondicaoPagamentoHandoff('');
    setDataEmissaoHandoff(new Date().toISOString().split('T')[0]);
    setShowPoModal(true);
  };

  const confirmPoHandoff = () => {
    if (!selectedCC) return toast.warn("Você deve selecionar um Centro de Custo para provisionar a despesa.");

    const unitPrice = Number(selectedWinnerForPo?.valor || 0);
    const qty = Number(selectedWinnerForPo?.quantidade || 1);
    const total = unitPrice * qty; 

    const draft = {
      source: 'cotacao',
      cotacao_id: selectedQuote?.id,
      cotacao_titulo: selectedQuote?.titulo || '',
      fornecedor_id: selectedWinnerForPo?.fornecedor_id || null,
      fornecedor_nome: selectedWinnerForPo?.fornecedor_nome || selectedWinnerForPo?.fornecedor || '',
      fornecedor_email: selectedWinnerForPo?.email_fornecedor || '',
      total_previsto: total,
      
      quantidade_item: qty,
      preco_unitario_item: unitPrice,

      prazo: selectedWinnerForPo?.prazo || '',
      centro_custo: selectedCC.codigo,
      observacoes: [
        selectedQuote?.observacoes ? `Parecer geral: ${selectedQuote.observacoes}` : null,
        selectedWinnerForPo?.obs ? `Obs da proposta vencedora: ${selectedWinnerForPo.obs}` : null,
      ].filter(Boolean).join('\n'),
      
      justificativa_solicitante: justificativaHandoff,
      condicao_pagamento: condicaoPagamentoHandoff,
      data_emissao: dataEmissaoHandoff,
      
      anexo_url: selectedWinnerForPo?.anexo_url || null,
      created_at: new Date().toISOString(),
    };

    localStorage.setItem('poDraftFromCotacao', JSON.stringify(draft));
    toast.info('Rascunho gerado. Redirecionando para Compras...', { autoClose: 1200 });
    window.location.href = '/corporativo/compras?fromCotacao=1';
  };

  return (
    <div className="cotacoes-container">
      <header className="cotacoes-header">
        <div className="header-title">
          <h2>Central de Compras</h2>
          <p>Gestão estratégica de fornecedores e orçamentos</p>
        </div>

        <div className="header-controls">
          <div className="view-toggle">
            <button className={viewMode === 'grid' ? 'active' : ''} onClick={() => setViewMode('grid')}><FaThLarge /></button>
            <button className={viewMode === 'table' ? 'active' : ''} onClick={() => setViewMode('table')}><FaList /></button>
          </div>
          <button className="btn-primary-create" onClick={() => setIsCreating(true)}>
            <FaPlusCircle /> Nova Disputa
          </button>
        </div>
      </header>

      <main className="cotacoes-content">
        {cotacoes.length === 0 && !loading ? (
          <div className="empty-state">
            <div className="empty-icon"><FaFolderOpen /></div>
            <h3>Nenhuma disputa ativa</h3>
            <p>Comece uma nova cotação para comparar preços.</p>
            <button className="btn-outline" onClick={() => setIsCreating(true)}>Criar Agora</button>
          </div>
        ) : (
          <>
            {viewMode === 'grid' ? (
              <div className="grid-layout">
                {cotacoes.map(q => (
                  <QuoteCard key={q.id} quote={q} onClick={() => { setSelectedQuote(q); setShowModal(true); }} isAdmin={isAdmin} onDelete={handleDelete} />
                ))}
              </div>
            ) : (
              <div className="table-layout card-shadow">
                <table>
                  <thead>
                    <tr>
                      <th>Status</th><th>Título</th><th>Data</th><th>Melhor Oferta (Total)</th><th>Vencedor</th><th>PO</th><th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {cotacoes.map(q => {
                      const itens = q.itens || [];
                      const min = itens.length ? Math.min(...itens.map(calcTotal)) : 0;
                      const win = itens.find(i => i.vencedor);
                      const winName = win ? (win.fornecedor_nome || win.fornecedor) : null;

                      return (
                        <tr key={q.id} onClick={() => { setSelectedQuote(q); setShowModal(true); }}>
                          <td><span className={`status-dot ${q.status === 'Aprovada' ? 'green' : 'orange'}`}></span> {q.status}</td>
                          <td className="fw-600">{q.titulo}</td>
                          <td>{new Date(q.data_criacao).toLocaleDateString()}</td>
                          <td className="text-success fw-bold">{min > 0 ? `R$ ${min.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : '-'}</td>
                          <td>{winName ? winName : <span className="text-muted">-</span>}</td>
                          <td>
                            {q.numero_po ? (
                              <span style={{ background: '#e0f2fe', color: '#0369a1', padding: '4px 8px', borderRadius: '6px', fontSize: '12px', fontWeight: 'bold' }}>
                                {q.numero_po}
                              </span>
                            ) : '-'}
                          </td>
                          <td>{isAdmin && <button className="btn-icon-table" onClick={(e) => handleDelete(e, q.id)}><FaTrash /></button>}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </main>

      {/* ✅ MODAL CRIAÇÃO COM CANCELAMENTO INTERCEPTADO */}
      {isCreating && (
        <div className="modal-backdrop">
          <div className="modal-panel slide-up" style={{ maxWidth: '900px', width: '90%' }}>
            <div className="modal-header-clean">
              <h3>Nova Disputa de Preços</h3>
              <button onClick={handleCancelCreation}><FaTimes /></button>
            </div>
            <div className="modal-body-form" style={{ padding: '25px 30px' }}>
              <div className="input-group-lg">
                <label>O que vamos comprar?</label>
                <input type="text" placeholder="Ex: Peças para Scania, Material de Escritório..." autoFocus value={newQuoteData.titulo} onChange={e => setNewQuoteData({ ...newQuoteData, titulo: e.target.value })} />
              </div>

              <div className="input-group-lg" style={{ marginTop: '20px' }}>
                <label><FaClipboardList style={{ marginRight: '5px' }} /> Observações / Parecer do Comprador (Geral)</label>
                <textarea placeholder="Detalhes adicionais do orçamento total..." value={newQuoteData.observacoes} onChange={e => setNewQuoteData({ ...newQuoteData, observacoes: e.target.value })} rows="2" style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #d1d9e6' }} />
              </div>

              <div className="suppliers-stack" style={{ marginTop: '25px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {newQuoteData.itens.map((item, idx) => (
                  <div key={idx} className="supplier-card-form" style={{ background: '#f8fafc', border: '1px solid #e2e8f0', padding: '20px', borderRadius: '12px' }}>
                    <div className="supplier-header-idx" style={{ marginBottom: '15px', borderBottom: '1px solid #cbd5e1', paddingBottom: '10px' }}>
                      <span style={{ fontWeight: 800, color: '#334155' }}>Concorrente {idx + 1}</span>
                      <button onClick={() => handleRemoveSupplier(idx)} tabIndex="-1" style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px' }}><FaMinusCircle /></button>
                    </div>

                    <div className="supplier-grid" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1.5fr', gap: '15px' }}>
                      <div style={{ gridColumn: '1 / span 2' }}>
                        <label style={{ fontSize: '13px', fontWeight: 600, color: '#64748b' }}>Fornecedor Cadastrado</label>
                        <FornecedorPicker
                          valueName={item.fornecedor_nome}
                          onSelect={(f) => {
                            handleInputChange(idx, 'fornecedor_id', f.id);
                            handleInputChange(idx, 'fornecedor_nome', f.razao_social);
                            handleInputChange(idx, 'fornecedor', f.razao_social);
                            handleInputChange(idx, 'email_fornecedor', f.email || '');
                          }}
                        />
                      </div>
                      
                      <div>
                        <label style={{ fontSize: '13px', fontWeight: 600, color: '#64748b' }}>E-mail de Contato</label>
                        <input className="inp-full" placeholder="exemplo@fornecedor.com" value={item.email_fornecedor} onChange={e => handleInputChange(idx, 'email_fornecedor', e.target.value)} />
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr 1fr', gap: '10px', gridColumn: '1 / -1', background: '#fff', padding: '15px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                        <div>
                          <label style={{ fontSize: '12px', fontWeight: 700, color: '#475569' }}>QTD.</label>
                          <input 
                            type="number" 
                            min="1" 
                            step="1" 
                            className="inp-full" 
                            style={{ fontWeight: 800, color: '#0f172a', textAlign: 'center' }} 
                            value={item.quantidade} 
                            onChange={e => handleInputChange(idx, 'quantidade', e.target.value)} 
                          />
                        </div>
                        <div className="money-input" style={{ display: 'flex', flexDirection: 'column' }}>
                          <label style={{ fontSize: '12px', fontWeight: 700, color: '#475569' }}>PREÇO UNIT. (R$)</label>
                          <div style={{ display: 'flex', alignItems: 'center', background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: '6px', padding: '0 10px' }}>
                            <span style={{ color: '#94a3b8', fontWeight: 600 }}>R$</span>
                            <input 
                              type="number" 
                              placeholder="0,00" 
                              style={{ border: 'none', background: 'transparent', width: '100%', padding: '10px', outline: 'none', fontWeight: 600 }} 
                              value={item.valor} 
                              onChange={e => handleInputChange(idx, 'valor', e.target.value)} 
                            />
                          </div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-end' }}>
                           <label style={{ fontSize: '12px', fontWeight: 700, color: '#475569' }}>TOTAL DESTA OPÇÃO</label>
                           <strong style={{ fontSize: '18px', color: '#10b981' }}>R$ {calcTotal(item).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</strong>
                        </div>
                      </div>

                      <div>
                        <label style={{ fontSize: '13px', fontWeight: 600, color: '#64748b' }}>Prazo de Entrega</label>
                        <input className="inp-full" placeholder="Ex: 5 dias" value={item.prazo} onChange={e => handleInputChange(idx, 'prazo', e.target.value)} />
                      </div>

                      <div style={{ gridColumn: '2 / span 2', position: 'relative' }}>
                         <label style={{ fontSize: '13px', fontWeight: 600, color: '#64748b' }}>Observações do Concorrente</label>
                        <input className="inp-full" style={{ paddingLeft: '35px' }} placeholder="Ex: Frete incluso, pagamento à vista..." value={item.obs} onChange={e => handleInputChange(idx, 'obs', e.target.value)} />
                        <FaCommentDots style={{ position: 'absolute', left: '12px', top: '35px', color: '#94a3b8' }} />
                      </div>

                      <div style={{ gridColumn: '1 / -1', marginTop: '5px' }}>
                        <label className={`file-upload-btn ${item.arquivo ? 'active' : ''}`} style={{ width: 'max-content' }}>
                          <input type="file" hidden onChange={e => handleFileChange(idx, e)} />
                          {item.arquivo ? <><FaCheckCircle /> Orçamento Anexado</> : <><FaPaperclip /> Anexar PDF do Orçamento</>}
                        </label>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <button className="btn-add-dashed" onClick={handleAddSupplier} style={{ marginTop: '20px' }}><FaPlusCircle /> Adicionar Outro Concorrente</button>
            </div>

            <div className="modal-footer-clean" style={{ padding: '20px 30px' }}>
              <button className="btn-text" onClick={handleCancelCreation}>Cancelar</button>
              <button className="btn-primary-lg" onClick={handleSave}>Lançar Disputa</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL INSIGHTS */}
      {showModal && selectedQuote && insights && (
        <div className="modal-backdrop-dark">
          <div className="insight-panel zoom-in">
            <div className="insight-header-hero">
              <div className="hero-content">
                <div className="hero-breadcrumbs"><span>Cotações</span> <FaArrowRight /> <span>Detalhes</span></div>
                <h1>{selectedQuote.titulo}</h1>
                <div className="hero-meta">
                  <span className={`pill-status ${selectedQuote.status === 'Aprovada' ? 'success' : 'pending'}`}>{selectedQuote.status}</span>
                  <span><FaCalendarAlt /> Criado em {new Date(selectedQuote.data_criacao).toLocaleDateString()}</span>
                </div>
              </div>
              <div className="hero-actions">
                {isAdmin && <button className="btn-hero-delete" onClick={(e) => handleDelete(e, selectedQuote.id)}><FaTrash /> Excluir</button>}
                <button className="close-hero" onClick={() => setShowModal(false)}><FaTimes /></button>
              </div>
            </div>

            <div className="insight-body-scroll">
              <div className="kpi-dashboard">
                <div className="kpi-box green-accent">
                  <FaTrophy className="kpi-icon" />
                  <div>
                    <small>Melhor Oferta Global (Total)</small>
                    <h3>R$ {insights.menorPreco.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</h3>
                  </div>
                </div>
                <div className="kpi-box blue-accent">
                  <FaMoneyBillWave className="kpi-icon" />
                  <div>
                    <small>Economia Estimada</small>
                    <h3>{insights.percentualEconomia}% <span className="kpi-sub">vs média</span></h3>
                  </div>
                </div>
                <div className={`kpi-box ai-trigger ${aiLoading ? 'loading' : ''} ${aiAnalysis ? 'done' : ''}`} onClick={!aiLoading ? handleGenerateAI : null}>
                  {aiLoading ? <div className="spinner-ring"></div> : <FaRobot className="kpi-icon" />}
                  <div>
                    <small>Consultor IA</small>
                    <h3>{aiLoading ? 'Analisando dados...' : (aiAnalysis ? 'Análise Completa' : 'Gerar Parecer Inteligente')}</h3>
                  </div>
                  {!aiAnalysis && !aiLoading && <FaMagic className="magic-sparkle" />}
                </div>
              </div>

              {selectedQuote.observacoes && (
                <div className="ai-result-box" style={{ borderColor: '#d1d9e6', background: '#f8f9fa' }}>
                  <div className="ai-title" style={{ color: '#444' }}><FaClipboardList /> Parecer Geral</div>
                  <div className="ai-text" style={{ fontStyle: 'italic', color: '#666' }}>{selectedQuote.observacoes}</div>
                </div>
              )}

              {aiAnalysis && (
                <div className="ai-result-box slide-down">
                  <div className="ai-title"><FaBrain /> Análise Estratégica Gemini</div>
                  <div className="ai-text">
                    {String(aiAnalysis).split('\n').map((l, i) => <p key={i}>{l}</p>)}
                  </div>
                </div>
              )}

              <h3 className="section-title">Comparativo de Propostas</h3>

              <div className="battle-grid">
                {selectedQuote.itens.map((item, idx) => {
                  const valorUnitario = parseFloat(item.valor) || 0;
                  const qtd = parseInt(item.quantidade, 10) || 1;
                  const valorTotal = calcTotal(item); 
                  
                  const dias = extractDays(item.prazo);
                  const isWinner = item.vencedor;
                  const isCheapest = valorTotal > 0 && valorTotal === insights.menorPreco;
                  const isFastest = dias > 0 && dias !== 9999 && dias === insights.menorPrazo;
                  const nome = item.fornecedor_nome || item.fornecedor;

                  return (
                    <div key={idx} className={`battle-card ${isWinner ? 'winner-card' : ''}`}>
                      <div className="floating-badges">
                        {isWinner && <span className="badge-winner"><FaTrophy /> VENCEDOR</span>}
                        {isCheapest && !isWinner && <span className="badge-best-price"><FaMedal /> MELHOR PREÇO</span>}
                        {isFastest && <span className="badge-fastest"><FaBolt /> MAIS RÁPIDO</span>}
                      </div>

                      <div className="card-top">
                        <div className="avatar-placeholder"><FaBuilding /></div>
                        <h4>{nome}</h4>
                        
                        <div className="big-price">
                          <small>R$</small><span>{valorTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                        </div>
                        <div style={{ fontSize: '13px', color: '#64748b', textAlign: 'center', marginTop: '-5px', marginBottom: '10px', background: '#f1f5f9', padding: '4px', borderRadius: '4px' }}>
                          <strong>{qtd} un</strong> x R$ {valorUnitario.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} unit.
                        </div>
                      </div>

                      <div className="card-details">
                        <div className="detail-row"><FaStopwatch /> <span>Prazo: {item.prazo || 'N/I'}</span></div>
                        <div className="detail-row"><FaCommentDots /> <span>Obs: {item.obs || 'Nenhuma observação'}</span></div>
                        {item.anexo_url && <div className="detail-row link"><FaPaperclip /> <span>Ver Proposta Original</span></div>}
                      </div>

                      <div className="card-actions">
                        {isWinner ? (
                          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                            {/* ✅ A MÁGICA DO BOTÃO ACOMPANHAR ACONTECE AQUI */}
                            {item.email_status ? (
                              <button className="btn-email-action" style={{ background: '#f59e0b', color: '#fff' }} onClick={() => { setSelectedTrackingItem(item); setShowTrackingModal(true); }}>
                                <FaHistory /> Acompanhar Cotação
                              </button>
                            ) : (
                              <button className="btn-email-action" onClick={() => openEmailModal(item)}>
                                <FaEnvelope /> Formalizar por E-mail
                              </button>
                            )}

                            {selectedQuote.numero_po ? (
                               <div style={{ display: 'flex', alignItems: 'center', flexGrow: 1, justifyContent: 'center', gap: 8, padding: '10px', background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: 8, color: '#334155', fontWeight: 600 }}>
                                 <FaShoppingCart color="#0ea5e9" /> PO Gerado: {selectedQuote.numero_po}
                               </div>
                            ) : (
                               isAdmin && (
                                 <button
                                   className="btn-approve-action"
                                   onClick={() => triggerPoGeneration(item)}
                                   title="Criar um Pedido de Compra a partir da proposta vencedora"
                                   style={{ background: '#0ea5e9' }}
                                 >
                                   <FaShoppingCart /> Gerar Pedido de Compra
                                 </button>
                               )
                            )}
                          </div>
                        ) : (
                          selectedQuote.status !== 'Aprovada' && isAdmin && (
                            <button className="btn-approve-action" onClick={() => handleApprove(item)} disabled={sendingEmail}>
                              {sendingEmail ? 'Processando...' : 'Aprovar Esta Proposta'}
                            </button>
                          )
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

            </div>
          </div>
        </div>
      )}

      {/* ✅ NOVO MODAL: ACOMPANHAR COTAÇÃO E HISTÓRICO (ESTILO THREAD) */}
      {showTrackingModal && selectedTrackingItem && (
        <div className="modal-backdrop" style={{ zIndex: 9999 }}>
          <div className="modal-panel slide-up" style={{ maxWidth: '900px', width: '95%', height: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            
            {/* HEADER DO MODAL */}
            <div className="modal-header-clean" style={{ borderBottom: '1px solid #e2e8f0', padding: '20px 25px', background: '#f8fafc' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
                <div>
                  <h3 style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '0 0 5px 0', color: '#0f172a', fontSize: '18px' }}>
                    <FaEnvelope color="#3b82f6" /> Histórico de Comunicação
                  </h3>
                  <div style={{ fontSize: '13px', color: '#64748b', display: 'flex', gap: '15px' }}>
                    <span><strong>Ref:</strong> {selectedQuote.titulo}</span>
                    <span><strong>Para:</strong> {selectedTrackingItem.email_fornecedor}</span>
                  </div>
                </div>
                <button onClick={() => setShowTrackingModal(false)} style={{ fontSize: '20px', color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer' }}><FaTimes /></button>
              </div>
            </div>
            
            <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
              
              {/* SIDEBAR ESQUERDA - STATUS E INFO */}
              <div style={{ width: '280px', background: '#f8fafc', borderRight: '1px solid #e2e8f0', padding: '20px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div style={{ background: '#fff', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '15px' }}>
                  <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 700, textTransform: 'uppercase', marginBottom: '5px' }}>Fornecedor</div>
                  <div style={{ fontSize: '15px', fontWeight: 800, color: '#0f172a', marginBottom: '2px' }}>{selectedTrackingItem.fornecedor_nome || selectedTrackingItem.fornecedor}</div>
                  <div style={{ fontSize: '12px', color: '#3b82f6' }}>{selectedTrackingItem.email_fornecedor}</div>
                </div>

                <div>
                  <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 700, textTransform: 'uppercase', marginBottom: '15px' }}>Status de Entrega</div>
                  <div style={{ borderLeft: '2px solid #e2e8f0', marginLeft: '8px', paddingLeft: '15px', display: 'flex', flexDirection: 'column', gap: '15px' }}>
                    
                    <div style={{ position: 'relative' }}>
                      <div style={{ position: 'absolute', left: '-22px', top: '2px', background: '#3b82f6', width: '12px', height: '12px', borderRadius: '50%', border: '2px solid #f8fafc' }}></div>
                      <strong style={{ fontSize: '13px', color: '#0f172a' }}>Enviado</strong>
                      <div style={{ fontSize: '11px', color: '#64748b' }}>Plataforma BLB System</div>
                    </div>

                    {selectedTrackingItem.email_status && (
                      <div style={{ position: 'relative' }}>
                        <div style={{ position: 'absolute', left: '-22px', top: '2px', background: ['delivered', 'opened', 'clicked'].includes(selectedTrackingItem.email_status) ? '#10b981' : '#f59e0b', width: '12px', height: '12px', borderRadius: '50%', border: '2px solid #f8fafc' }}></div>
                        <strong style={{ fontSize: '13px', color: '#0f172a' }}>
                          {selectedTrackingItem.email_status === 'enviado' && 'Aguardando Servidor...'}
                          {selectedTrackingItem.email_status === 'delivered' && 'Entregue na Caixa'}
                          {selectedTrackingItem.email_status === 'opened' && 'Aberto (Lido) 👀'}
                          {selectedTrackingItem.email_status === 'clicked' && 'Link Clicado 👆'}
                          {(selectedTrackingItem.email_status === 'bounce' || selectedTrackingItem.email_status === 'hard_bounce' || selectedTrackingItem.email_status === 'blocked') && <span style={{ color: '#ef4444' }}>Falha na Entrega 🔴</span>}
                        </strong>
                        <div style={{ fontSize: '11px', color: '#64748b' }}>Rastreio Brevo</div>
                      </div>
                    )}
                  </div>
                </div>

                <div style={{ padding: '12px', background: '#e0f2fe', borderRadius: '8px', border: '1px solid #bae6fd', marginTop: 'auto' }}>
                  <FaInfoCircle color="#0369a1" style={{ marginBottom: '5px' }} />
                  <div style={{ fontSize: '11px', color: '#0369a1', lineHeight: '1.4' }}>
                    As respostas enviadas pelo fornecedor não aparecem aqui. Elas são direcionadas automaticamente para o seu <strong>Zoho Mail</strong>.
                  </div>
                </div>
              </div>

              {/* ÁREA PRINCIPAL - A THREAD DE E-MAILS */}
              <div style={{ flex: 1, padding: '20px', overflowY: 'auto', background: '#fff', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                
                {/* BOLHA 1: O E-MAIL ENVIADO */}
                <div style={{ alignSelf: 'flex-end', width: '90%', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '12px 12px 0 12px', padding: '20px', position: 'relative' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px', borderBottom: '1px solid #cbd5e1', paddingBottom: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{ width: '32px', height: '32px', background: '#3b82f6', color: '#fff', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800 }}>BLB</div>
                      <div>
                        <div style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a' }}>Equipe de Compras (Você)</div>
                        <div style={{ fontSize: '11px', color: '#64748b' }}>Para: {selectedTrackingItem.email_fornecedor}</div>
                      </div>
                    </div>
                  </div>
                  
                  <div style={{ fontSize: '14px', color: '#334155', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
                    Olá {selectedTrackingItem.fornecedor_nome || selectedTrackingItem.fornecedor},<br/><br/>
                    Parabéns! Informamos que sua proposta para "{selectedQuote.titulo}" foi a vencedora.<br/><br/>
                    <strong>VALOR CONFIRMADO: R$ {calcTotal(selectedTrackingItem).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</strong><br/><br/>
                    Por favor, siga com a emissão da Nota Fiscal e entrega conforme combinado.<br/>
                    Qualquer divergência de valores ou prazos, favor responder este e-mail imediatamente.<br/><br/>
                    Atenciosamente,<br/>
                    Equipe de Compras
                  </div>
                  
                  {selectedTrackingItem.anexo_url && (
                    <div style={{ marginTop: '15px', display: 'inline-flex', alignItems: 'center', gap: '8px', background: '#fff', border: '1px solid #cbd5e1', padding: '6px 12px', borderRadius: '6px', fontSize: '12px', color: '#3b82f6', fontWeight: 600 }}>
                      <FaPaperclip /> Proposta_Original.pdf (Anexado)
                    </div>
                  )}
                </div>

                {/* ESPAÇO PARA O BOTÃO DE ENCERRAR E AVISO */}
                <div style={{ textAlign: 'center', margin: '20px 0' }}>
                  <div style={{ fontSize: '12px', color: '#94a3b8', background: '#f8fafc', padding: '4px 12px', borderRadius: '99px', display: 'inline-block' }}>
                    Aguardando retorno do fornecedor via Zoho Mail
                  </div>
                </div>

              </div>
            </div>

            {/* FOOTER DO MODAL */}
            <div className="modal-footer-clean" style={{ borderTop: '1px solid #e2e8f0', padding: '15px 25px', background: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <button className="btn-text" onClick={() => setShowTrackingModal(false)} style={{ color: '#64748b', fontWeight: 600 }}>Fechar</button>
              <button 
                className="btn-danger" 
                style={{ padding: '10px 20px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', transition: 'background 0.2s' }} 
                onMouseEnter={(e) => e.target.style.background = '#dc2626'}
                onMouseLeave={(e) => e.target.style.background = '#ef4444'}
                onClick={() => handleEncerrarConversa(selectedTrackingItem)}
              >
                <FaBan /> Encerrar Conversa e Enviar Aviso
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: SELECIONAR CENTRO DE CUSTO ANTES DE GERAR O PO */}
      {showPoModal && selectedWinnerForPo && (
        <div className="modal-backdrop" style={{ zIndex: 9999 }}>
          <div className="modal-panel slide-up" style={{ maxWidth: '600px', width: '100%', overflow: 'visible' }}>
            
            <div className="modal-header-clean" style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: '15px' }}>
              <h3 style={{ display: 'flex', alignItems: 'center', gap: 10 }}><FaSitemap color="#0ea5e9" /> Provisionar Despesa</h3>
              <button onClick={() => setShowPoModal(false)}><FaTimes /></button>
            </div>
            
            <div className="modal-body-form" style={{ padding: '20px 25px', overflow: 'visible' }}>
              <div style={{ marginBottom: '20px', background: '#f8fafc', padding: '15px', borderRadius: '8px', border: '1px dashed #cbd5e1' }}>
                <p style={{ margin: 0, fontSize: '13px', color: '#64748b' }}>Fornecedor Vencedor</p>
                <strong style={{ fontSize: '16px', color: '#334155' }}>{selectedWinnerForPo.fornecedor_nome || selectedWinnerForPo.fornecedor}</strong>
                
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px' }}>
                  <span style={{ fontSize: '13px', color: '#64748b' }}>Valor Total da Proposta:</span>
                  <strong style={{ color: '#10b981' }}>R$ {calcTotal(selectedWinnerForPo).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</strong>
                </div>
              </div>

              <div className="input-group-lg" style={{ overflow: 'visible' }}>
                <label style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Selecione o Centro de Custo</span>
                  <span style={{ color: '#ef4444', fontSize: '12px' }}>*Obrigatório</span>
                </label>
                
                <CCPicker onSelect={(cc) => setSelectedCC(cc)} />
                
                {selectedCC && (
                   <div style={{ marginTop: '10px', fontSize: '13px', color: '#0369a1', background: '#e0f2fe', padding: '10px', borderRadius: '6px' }}>
                     ✅ CC Vinculado: <strong>{selectedCC.codigo} - {selectedCC.nome_area}</strong>
                   </div>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginTop: '20px' }}>
                <div className="input-group-lg" style={{ margin: 0 }}>
                  <label>Condição de Pagamento</label>
                  <input 
                    className="inp-full"
                    style={{ padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                    placeholder="Ex: 30/60/90 dias, À vista..."
                    value={condicaoPagamentoHandoff}
                    onChange={(e) => setCondicaoPagamentoHandoff(e.target.value)}
                  />
                </div>
                <div className="input-group-lg" style={{ margin: 0 }}>
                  <label>Data de Emissão</label>
                  <input 
                    type="date"
                    className="inp-full"
                    style={{ padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                    value={dataEmissaoHandoff}
                    onChange={(e) => setDataEmissaoHandoff(e.target.value)}
                  />
                </div>
              </div>

              <div className="input-group-lg" style={{ overflow: 'visible', marginTop: '20px' }}>
                <label style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                  <span style={{ color: '#4f46e5', fontWeight: 600 }}>Justificativa para o Aprovador/Diretor</span>
                  <span style={{ color: '#64748b', fontSize: '12px' }}>(Opcional)</span>
                </label>
                <textarea 
                  style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #c7d2fe', backgroundColor: '#f8fafc', resize: 'vertical', fontSize: '0.9rem', outline: 'none' }}
                  rows={3}
                  placeholder="Defenda a necessidade desta compra para agilizar sua aprovação..."
                  value={justificativaHandoff}
                  onChange={(e) => setJustificativaHandoff(e.target.value)}
                />
              </div>
            </div> 

            <div className="modal-footer-clean" style={{ borderTop: '1px solid #e2e8f0', padding: '15px 25px', background: '#f8fafc', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button className="btn-text" onClick={() => setShowPoModal(false)}>Cancelar</button>
              <button 
                className="btn-primary-lg" 
                style={{ opacity: selectedCC ? 1 : 0.5, cursor: selectedCC ? 'pointer' : 'not-allowed' }}
                onClick={selectedCC ? confirmPoHandoff : null}
              >
                <FaCheckCircle style={{ marginRight: 8 }}/> Enviar para Compras
              </button>
            </div>

          </div>
        </div>
      )}

      {/* MODAL DE EMAIL WORKSTATION */}
      {showEmailModal && (
        <div className="modal-backdrop email-layer">
          <div className="email-panel-wide slide-up">
            <aside className="email-sidebar">
              <div className="sidebar-header">
                <h3>Resumo do Pedido</h3>
                <p>Cotação #{selectedQuote?.id}</p>
              </div>

              <div className="sidebar-card">
                <label>Fornecedor</label>
                <div className="val-box">
                  <FaBuilding className="icon-muted" />
                  <strong>{emailData.winnerName}</strong>
                </div>
              </div>

              <div className="sidebar-card">
                <label>Valor Acordado</label>
                <div className="val-box highlight">
                  <FaMoneyBillWave className="icon-green" />
                  <strong>R$ {emailData.winnerPrice}</strong>
                </div>
              </div>

              <div className="sidebar-divider"></div>

              <div className="sidebar-attachment-section">
                <label>Documentos</label>
                {emailData.attachmentName ? (
                  <div className="file-chip">
                    <FaSearch className="text-red" /> {emailData.attachmentName}
                  </div>
                ) : (
                  <div className="upload-container">
                    {emailData.customFile ? (
                      <div className="file-chip success">
                        <FaCheckCircle className="text-green" /> {emailData.customFile.name}
                      </div>
                    ) : (
                      <label className="btn-upload-post">
                        <FaCloudUploadAlt /> Anexar Pedido/Contrato
                        <input type="file" hidden onChange={handleEmailAttachment} />
                      </label>
                    )}
                    <p className="upload-hint">Anexe o PDF para enviar junto</p>
                  </div>
                )}
              </div>
            </aside>

            <section className="email-composer">
              <div className="composer-header">
                <h2>Formalização de Compra</h2>
                <button onClick={() => setShowEmailModal(false)} className="btn-close-circle"><FaTimes /></button>
              </div>

              <div className="composer-body">
                <div className="input-row">
                  <label>Para:</label>
                  <div className="input-wrapper">
                    <input
                      type="email"
                      className="inp-seamless"
                      placeholder="exemplo@fornecedor.com"
                      value={emailData.to} 
                      onChange={e => setEmailData({ ...emailData, to: e.target.value })} 
                      autoFocus
                    />
                    <FaUserEdit className="input-icon" />
                  </div>
                </div>

                <div className="input-row">
                  <label>Assunto:</label>
                  <input
                    className="inp-seamless"
                    value={emailData.subject}
                    onChange={e => setEmailData({ ...emailData, subject: e.target.value })}
                  />
                </div>

                <div className="editor-container">
                  <textarea
                    className="textarea-seamless"
                    value={emailData.body}
                    onChange={e => setEmailData({ ...emailData, body: e.target.value })}
                    placeholder="Digite a mensagem oficial..."
                  />
                </div>
              </div>

              <div className="composer-footer">
                <button className="btn-ghost" onClick={() => setShowEmailModal(false)}>Cancelar</button>
                <button className="btn-send-wide" onClick={sendEmailFinal}>
                  <FaPaperPlane /> Enviar Pedido Oficial
                </button>
              </div>
            </section>
          </div>
        </div>
      )}
    </div>
  );
}